/**
 * TTS speech: voice selection and speaking one phrase via Web Speech API.
 * Used by the TTS feature when processing the queue.
 */

const PREFER_VOICE_NAMES = /natural|neural|aria|jenny|sara|mark|online|premium|daniel|samantha|karen|moira|alice|amelie/i;
const AVOID_VOICE_NAMES = /microsoft david|microsoft zira|microsoft mark\b|google us english\b|desktop\b/i;

export function scoreVoice(v: SpeechSynthesisVoice, preferredLang: string): number {
  const name = (v.name || '').toLowerCase();
  const lang = (v.lang || '').toLowerCase();
  let s = PREFER_VOICE_NAMES.test(name) ? 100 : 0;
  s += AVOID_VOICE_NAMES.test(name) ? -50 : 0;
  s += preferredLang && lang.startsWith(preferredLang) ? 10 : 0;
  s += lang.startsWith('en') ? 5 : 0;
  s += v.default ? 3 : 0;
  return s;
}

export function pickBestVoice(
  voices: SpeechSynthesisVoice[],
  preferredLang: string,
  storedVoiceURI: string | null
): SpeechSynthesisVoice | null {
  if (!voices.length) return null;
  if (storedVoiceURI) {
    const found = voices.find((v) => v.voiceURI === storedVoiceURI || v.name === storedVoiceURI);
    if (found) return found;
  }
  const lang = preferredLang || 'en';
  const filtered = voices.filter((v) => {
    const vLang = (v.lang || '').toLowerCase();
    return vLang.startsWith(lang) || (!lang.match(/^en$/) && vLang.startsWith('en'));
  });
  const list = filtered.length ? filtered : voices;
  list.sort((a, b) => scoreVoice(b, lang) - scoreVoice(a, lang));
  return list[0] || null;
}

export interface SpeakOneOpts {
  rate: number;
  volume: number;
  voice: SpeechSynthesisVoice | null;
}

export function speakOne(
  synth: SpeechSynthesis,
  text: string,
  opts: SpeakOneOpts,
  callbacks: { onStart?: () => void; onEnd?: () => void; onError?: () => void }
): void {
  const UtteranceCtor = typeof SpeechSynthesisUtterance !== 'undefined'
    ? SpeechSynthesisUtterance
    : (typeof window !== 'undefined' && (window as unknown as { webkitSpeechSynthesisUtterance?: new (text: string) => SpeechSynthesisUtterance }).webkitSpeechSynthesisUtterance);
  if (!UtteranceCtor) {
    callbacks.onEnd?.();
    return;
  }
  const u = new UtteranceCtor(text);
  if (opts.voice) u.voice = opts.voice;
  u.rate = opts.rate;
  u.volume = opts.volume;
  u.onstart = callbacks.onStart;
  u.onend = callbacks.onEnd;
  u.onerror = callbacks.onError;
  synth.speak(u);
}
